/**
 * Product page behaviour for the Saadhu theme.
 *
 * <saadhu-sticky-bar>: mobile bar (name, price, Add) that appears once the main Add button has
 * scrolled out of view above the viewport. It sits inside <saadhu-size-gate>, reads its state from
 * the `saadhu:size` event, and never covers the footer or a cookie banner.
 *
 * Accordions: opens the <details> that matches the URL hash (#materials, #size-guide).
 */
class SaadhuStickyBar extends HTMLElement {
  /** @type {AbortController | null} */
  #abort = null;

  /** @type {IntersectionObserver | null} */
  #observer = null;

  #pastButton = false;
  #footerVisible = false;

  connectedCallback() {
    this.#abort?.abort();
    this.#abort = new AbortController();
    const { signal } = this.#abort;

    /** @type {HTMLElement | null} */
    this.gate = this.closest('saadhu-size-gate');
    const mainButton = this.gate?.querySelector('button[name="add"]');
    this.button = this.querySelector('[data-saadhu-sticky-add]');
    if (!this.gate || !mainButton || !this.button) return;

    this.gate.addEventListener('saadhu:size', (event) => this.#syncLabel(event.detail.gated), { signal });
    this.button.addEventListener('click', this.#onClick, { signal });

    this.#observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (entry.target === mainButton) {
          this.#pastButton = !entry.isIntersecting && entry.boundingClientRect.top < 0;
        } else {
          this.#footerVisible = entry.isIntersecting;
        }
      }
      this.#update();
    });
    this.#observer.observe(mainButton);
    const footer = document.querySelector('footer, .shopify-section-group-footer-group');
    if (footer) this.#observer.observe(footer);

    // A cookie banner can appear after load: keep the bar above it.
    new MutationObserver(() => this.#syncOffset()).observe(document.body, { childList: true });
    window.addEventListener('resize', () => this.#syncOffset(), { signal });
    this.#syncLabel(Boolean(this.gate.gated));
    this.#syncOffset();
  }

  disconnectedCallback() {
    this.#abort?.abort();
    this.#observer?.disconnect();
    this.#abort = null;
  }

  #update() {
    this.hidden = !this.#pastButton || this.#footerVisible;
    this.#syncOffset();
  }

  /** @param {boolean} gated */
  #syncLabel(gated) {
    if (!this.button || this.button.disabled) return;
    this.button.textContent = gated ? this.dataset.labelSelect ?? '' : this.dataset.labelAdd ?? '';
    this.button.setAttribute('aria-disabled', String(gated));
  }

  #syncOffset() {
    const banner = document.querySelector('#shopify-pc__banner');
    const height = banner instanceof HTMLElement && banner.offsetHeight ? banner.offsetHeight : 0;
    this.style.setProperty('--saadhu-sticky-offset', `${height}px`);
  }

  #onClick = () => {
    const gate = /** @type {any} */ (this.gate);
    if (gate?.gated) gate.requireSize();
    else gate?.addButton?.click();
  };
}

if (!customElements.get('saadhu-sticky-bar')) customElements.define('saadhu-sticky-bar', SaadhuStickyBar);

function openHashAccordion() {
  const target = location.hash ? document.getElementById(decodeURIComponent(location.hash.slice(1))) : null;
  if (target instanceof HTMLDetailsElement) {
    target.open = true;
    target.scrollIntoView();
  }
}

openHashAccordion();
window.addEventListener('hashchange', openHashAccordion);
